import Header from '../components/Header'
import Footer from '../components/Footer'
import { Link } from 'react-router-dom'

const GoogleFacebookAds = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="relative bg-gray-900">
          <div className="absolute inset-0">
            <img
              className="w-full h-full object-cover opacity-30"
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2850&q=80"
              alt="Google and Facebook Ads"
            />
          </div>
          <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
                <span className="block">Google & Facebook Ads</span>
                <span className="block text-indigo-400">That Drive Real Results</span>
              </h1>
              <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-100">
                Data-driven paid advertising strategies that deliver qualified leads and maximize your ad spend ROI.
              </p>
              <div className="mt-10">
                <Link
                  to="/contact"
                  className="inline-block bg-indigo-600 py-3 px-8 border border-transparent rounded-md text-base font-medium text-white hover:bg-indigo-700"
                >
                  Get Your Free Audit
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { name: 'Average ROI Increase', value: '320%' },
                { name: 'Cost Per Lead Reduction', value: '40-60%' },
                { name: 'Click-Through Rate Boost', value: '2-3x' },
                { name: 'Conversion Rate Lift', value: '50-150%' },
              ].map((stat) => (
                <div key={stat.name} className="text-center p-6 bg-gray-50 rounded-lg shadow">
                  <p className="text-3xl font-bold text-indigo-600">{stat.value}</p>
                  <p className="mt-2 text-sm font-medium text-gray-500">{stat.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Services Section - Google Ads */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:text-center">
              <h2 className="text-base text-indigo-600 font-semibold tracking-wide uppercase">Google Ads</h2>
              <p className="mt-2 text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-4xl">
                Capture High-Intent Search Traffic
              </p>
              <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
                Reach customers actively searching for your products or services.
              </p>
            </div>

            <div className="mt-16">
              <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-3 md:gap-x-8 md:gap-y-10">
                {[
                  {
                    name: 'Search Ads',
                    description: 'Text ads that appear on Google search results',
                    features: ['Keyword research', 'Ad copy optimization', 'Bid management', 'Negative keywords']
                  },
                  {
                    name: 'Display Network',
                    description: 'Visual ads across millions of websites',
                    features: ['Audience targeting', 'Placement selection', 'Creative design', 'Remarketing']
                  },
                  {
                    name: 'Shopping Ads',
                    description: 'Product listings for eCommerce businesses',
                    features: ['Feed optimization', 'Competitive analysis', 'Campaign structure', 'ROAS tracking']
                  },
                  {
                    name: 'YouTube Ads',
                    description: 'Video advertising on the world\'s #1 platform',
                    features: ['Video production', 'Placement strategy', 'View-through tracking', 'Audience building']
                  },
                  {
                    name: 'Performance Max',
                    description: 'Automated campaigns across Google networks',
                    features: ['Asset creation', 'Goal optimization', 'Cross-channel reporting', 'Smart bidding']
                  },
                  {
                    name: 'Local Service Ads',
                    description: 'Lead generation for service area businesses',
                    features: ['Profile optimization', 'Lead filtering', 'Review management', 'Budget allocation']
                  }
                ].map((service) => (
                  <div key={service.name} className="relative bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                    <h3 className="text-lg font-medium text-gray-900">
                      {service.name}
                    </h3>
                    <p className="mt-2 text-sm text-gray-500">
                      {service.description}
                    </p>
                    <ul className="mt-4 space-y-2">
                      {service.features.map((feature) => (
                        <li key={feature} className="flex items-start">
                          <svg className="flex-shrink-0 h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="ml-2 text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Services Section - Facebook Ads */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:text-center">
              <h2 className="text-base text-indigo-600 font-semibold tracking-wide uppercase">Facebook & Instagram Ads</h2>
              <p className="mt-2 text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-4xl">
                Targeted Social Advertising
              </p>
              <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
                Reach your ideal customers where they spend their time online.
              </p>
            </div>

            <div className="mt-16">
              <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-3 md:gap-x-8 md:gap-y-10">
                {[
                  {
                    name: 'Lead Generation',
                    description: 'Capture quality leads directly on Facebook',
                    features: ['Form optimization', 'Lead nurturing', 'CRM integration', 'Cost-per-lead tracking']
                  },
                  {
                    name: 'Conversion Campaigns',
                    description: 'Drive sales and website actions',
                    features: ['Pixel implementation', 'Event tracking', 'Lookalike audiences', 'Retargeting']
                  },
                  {
                    name: 'Brand Awareness',
                    description: 'Increase visibility and recognition',
                    features: ['Creative testing', 'Audience research', 'Frequency capping', 'Brand lift studies']
                  },
                  {
                    name: 'Retargeting',
                    description: 'Re-engage website visitors',
                    features: ['Audience segmentation', 'Creative sequencing', 'Exclusion lists', 'Cross-platform']
                  },
                  {
                    name: 'Stories Ads',
                    description: 'Full-screen immersive ads',
                    features: ['Vertical creative', 'Swipe-up links', 'Interactive elements', 'Story sequencing']
                  },
                  {
                    name: 'Messenger Ads',
                    description: 'Start conversations with prospects',
                    features: ['Chat automation', 'Quick replies', 'Sponsored messages', 'Bot integration']
                  }
                ].map((service) => (
                  <div key={service.name} className="relative bg-gray-50 p-6 rounded-lg border border-gray-200 shadow-sm">
                    <h3 className="text-lg font-medium text-gray-900">
                      {service.name}
                    </h3>
                    <p className="mt-2 text-sm text-gray-500">
                      {service.description}
                    </p>
                    <ul className="mt-4 space-y-2">
                      {service.features.map((feature) => (
                        <li key={feature} className="flex items-start">
                          <svg className="flex-shrink-0 h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="ml-2 text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="bg-indigo-700 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-white text-center">
              Our 5-Step Advertising Process
            </h2>
            <div className="mt-12">
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-5">
                {[
                  {
                    name: 'Strategy',
                    description: 'Goal setting & audience research'
                  },
                  {
                    name: 'Setup',
                    description: 'Account structure & tracking'
                  },
                  {
                    name: 'Launch',
                    description: 'Campaign creation & optimization'
                  },
                  {
                    name: 'Analyze',
                    description: 'Performance tracking & reporting'
                  },
                  {
                    name: 'Scale',
                    description: 'Budget increases & expansion'
                  }
                ].map((step, index) => (
                  <div key={step.name} className="relative text-center">
                    <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-indigo-500 rounded-full h-10 w-10 flex items-center justify-center">
                      <span className="text-white font-bold">{index + 1}</span>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow h-full pt-12">
                      <h3 className="text-lg font-medium text-gray-900">{step.name}</h3>
                      <p className="mt-2 text-sm text-gray-500">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div id="contact" className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Ready to Scale Your Business with Paid Ads?
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              Get expert management for your Google and Facebook advertising campaigns.
            </p>
            <div className="mt-8 flex justify-center">
              <div className="inline-flex rounded-md shadow">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Get Started Today
                </Link>
              </div>
              <div className="ml-3 inline-flex">
                <Link
                  to="/case-studies"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
                >
                  View Case Studies
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default GoogleFacebookAds
